import React from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined, FilterFilled, SearchOutlined } from '@ant-design/icons';
import { Button, Table } from 'antd';
import { LandlordNoProperty as LandlordNoPropertyIcon } from 'common/components/svgs';
import isEqual from 'lodash/isEqual';
import { bookingJourneyType } from 'common/constants/booking';
import { propertySourceType, syncType } from 'common/constants/property';
import { propertyStatus } from '~constants/landlord';
import SearchComponent from '~components/search-component';
import TableColumnSearch from '~components/table-column-search';
import FilterDropdown from 'common/components/filter';
import generatePath from '~settings/routing';
import BulkEditModal from './bulkEditModal';
import SyncType from 'common/components/sync-type';

export default class LandlordPropertyRelated extends React.Component {
  constructor() {
    super();
    this.state = {
      filters: {
        pageNumber: 1,
        pageSize: 10,
        slugs: [],
        citySlug: null,
        countrySlugs: [],
        statuses: [],
        bookNowEnable: [],
        bookingJourneys: [],
      },
      visibility: {
        propertyName: false,
        displayCity: false,
        displayCountry: false,
        bookingJourney: false,
      },
      propertyNameList: [],
      displayCountryList: [],
      isWithoutFilter: true,
      isBulkEditModalVisible: false,
      cityFilter: '',
      propertyNameListHasMore: true,
      propertyNameListPageNumber: 1,
      propertyNameListLoading: false,
      propertyNameListKey: '',
    };

    this.propertyPageSize = 10;
  }
  componentDidMount() {
    this.getNewProperties();
  }
  getNewProperties = () => {
    const {
      slugs,
      citySlug,
      countrySlugs,
      statuses,
      bookNowEnable,
      bookingJourneys,
      externalSystems,
    } = this.state.filters;

    if (
      citySlug ||
      slugs.length > 0 ||
      countrySlugs.length > 0 ||
      (statuses && statuses.length > 0) ||
      bookNowEnable.length > 0 ||
      (bookingJourneys && bookingJourneys.length > 0) ||
      (externalSystems && externalSystems?.length > 0)
    ) {
      this.setState({ isWithoutFilter: false });
    }
    const propertiesParams = Object.assign({}, this.state.filters, {
      landlordSlug: this.props.landlordSlug,
      bookNowEnable: this.state.filters.bookNowEnable[0],
    });

    this.props.getPropertiesByLandlordSlug(propertiesParams);
  };

  handleTableChange = (pagination, filters) => {
    this.setState(
      prevState => ({
        filters: {
          ...prevState.filters,
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          bookNowEnable: filters.bookNowEnable,
          statuses: filters.status,
        },
      }),
      () => {
        this.getNewProperties();
      },
    );
  };

  handlePropertyChange = value => {
    if (!value.currentItem) {
      this.setState({
        propertyNameList: [...this.state.propertyNameList],
      });
    }
  };

  handlePropertyBlur = value => {
    const slugs = value.value.map(landlord => landlord.slug);
    if (isEqual(slugs, this.state.filters.slugs)) {
      return;
    }
    this.setState(
      prevState => ({
        filters: {
          ...prevState.filters,
          slugs,
          pageNumber: 1,
        },
      }),
      () => {
        this.getNewProperties();
      },
    );
  };

  handlePropertySearch = value => {
    this.setState({
      propertyNameListLoading: true,
    });
    this.props.searchPropertyNameList(
      {
        landlordSlug: this.props.landlordSlug,
        propertyName: value,
        pageNumber: 1,
        pageSize: this.propertyPageSize,
      },
      res => {
        this.setState({
          propertyNameListKey: value,
          propertyNameListPageNumber: 1,
          propertyNameListHasMore: res?.properties?.totalCount > this.propertyPageSize,
          propertyNameList: res?.properties?.edges?.map(property => property.node),
          propertyNameListLoading: false,
        });
      },
    );
  };

  handlePropertyScrollEnd = () => {
    if (this.state.propertyNameListHasMore) {
      this.setState({
        propertyNameListLoading: true,
      });
      this.props.searchPropertyNameList(
        {
          landlordSlug: this.props.landlordSlug,
          propertyName: this.state.propertyNameListKey,
          pageNumber: this.state.propertyNameListPageNumber + 1,
          pageSize: this.propertyPageSize,
        },
        res => {
          this.setState(prev => ({
            propertyNameListPageNumber: prev.propertyNameListPageNumber + 1,
            propertyNameListHasMore: res?.properties?.totalCount > this.propertyPageSize,
            propertyNameList: [
              ...prev.propertyNameList,
              ...res?.properties?.edges?.map(property => property.node),
            ],
            propertyNameListLoading: false,
          }));
        },
      );
    }
  };

  getTableColumns = () => {
    const { t } = this.props;
    const { filters, propertyNameList, displayCountryList } = this.state;
    return [
      {
        title: t('cms.landlord.property_related.property_name.table'),
        dataIndex: 'propertyName',
        width: '15%',
        key: 'propertyName',
        render: propertyName => propertyName || '-',
        filterDropdown: (
          <div>
            <label
              ref={node => {
                this.propertyNameTargetNode = node;
              }}
            />
            <SearchComponent
              t={t}
              targetInput={this.propertyNameTargetNode}
              options={propertyNameList}
              isLoadingByScroll={true}
              onSearch={this.handlePropertySearch}
              onBlur={this.handlePropertyBlur}
              onChange={this.handlePropertyChange}
              onScrollEnd={this.handlePropertyScrollEnd}
              keyValue="slug"
              showSelectAll={false}
              isFetching={this.state.propertyNameListLoading}
            />
          </div>
        ),
        filterIcon: (
          <SearchOutlined style={{ color: filters.slugs.length > 0 ? '#38b2a6' : '#c8c9cb' }} />
        ),
        filterDropdownVisible: this.state.visibility.propertyName,
        onFilterDropdownVisibleChange: view => {
          if (view && this.propertyNameTargetNode) {
            setTimeout(() => {
              this.propertyNameTargetNode.click();
            }, 0);
          }
          this.setState(prevState => ({
            visibility: {
              ...prevState.visibility,
              propertyName: view,
            },
          }));
        },
      },
      {
        title: t('cms.landlord.property_related.booking_type.table'),
        dataIndex: 'bookingJourney',
        width: '10%',
        key: 'bookingJourney',
        render: bookingJourney => bookingJourney || '-',
        filterDropdown: (
          <FilterDropdown
            filterDropdownVisible={this.state.visibility.bookingJourney}
            options={[
              {
                label: t(`properties_booking_type_${bookingJourneyType.MANUAL.toLowerCase()}`),
                value: bookingJourneyType.MANUAL,
              },
              {
                label: t(
                  `properties_booking_type_${bookingJourneyType.SEMI_AUTOMATIC.toLowerCase()}`,
                ),
                value: bookingJourneyType.SEMI_AUTOMATIC,
              },
              {
                label: t(
                  `properties_booking_type_${bookingJourneyType.FULL_AUTOMATIC.toLowerCase()}`,
                ),
                value: bookingJourneyType.FULL_AUTOMATIC,
              },
              {
                label: t(
                  `properties_booking_type_${bookingJourneyType.INSTANT_BOOK.toLowerCase()}`,
                ),
                value: bookingJourneyType.INSTANT_BOOK,
              },
              {
                label: t(
                  `properties_booking_type_${bookingJourneyType.LEAD_GENERATION.toLowerCase()}`,
                ),
                value: bookingJourneyType.LEAD_GENERATION,
              },
            ]}
            submitHandle={bookingJourneys => {
              this.setState(
                prevState => ({
                  filters: {
                    ...prevState.filters,
                    bookingJourneys,
                    pageNumber: 1,
                  },
                  visibility: {
                    ...prevState.visibility,
                    bookingJourney: false,
                  },
                }),
                () => {
                  this.getNewProperties();
                },
              );
            }}
          />
        ),
        filterDropdownVisible: this.state.visibility.bookingJourney,
        onFilterDropdownVisibleChange: visible => {
          this.setState(prevState => ({
            visibility: {
              ...prevState.visibility,
              bookingJourney: visible,
            },
          }));
        },
        filterIcon: (
          <FilterFilled
            style={{
              color:
                filters.bookingJourneys && filters.bookingJourneys.length > 0
                  ? '#38b2a6'
                  : '#c8c9cb',
              backgroundColor: 'transparent',
            }}
          />
        ),
      },
      {
        title: t('cms:cms.properties.detail.sync_type'),
        dataIndex: 'externalSystem',
        width: '10%',
        render: externalSystem => <SyncType t={t} externalSystem={externalSystem} />,
        filterDropdown: (
          <FilterDropdown
            filterDropdownVisible={this.state.visibility.externalSystem}
            options={[
              {
                label: t(`cms:cms.properties.detail.sync_type.integration.title`),
                value: syncType.INTEGRATION,
              },
              {
                label: t(`cms:cms.properties.detail.sync_type.open_api`),
                value: syncType.OPEN_API,
              },
            ]}
            submitHandle={externalSystem => {
              let externalSystemParam = [];
              if (externalSystem.includes(syncType.INTEGRATION)) {
                externalSystemParam.push(propertySourceType.INTEGRATION);
                externalSystemParam.push(propertySourceType.SALESFORCE);
                externalSystemParam.push(propertySourceType.ENTRATA);
              }

              if (externalSystem.includes(syncType.OPEN_API)) {
                externalSystemParam.push(propertySourceType.PUBLIC_GATEWAY);
              }

              this.setState(
                prevState => ({
                  filters: {
                    ...prevState.filters,
                    externalSystems: externalSystemParam,
                    pageNumber: 1,
                  },
                  visibility: {
                    ...prevState.visibility,
                    externalSystem: false,
                  },
                }),
                () => {
                  this.getNewProperties();
                },
              );
            }}
          />
        ),
        filterDropdownVisible: this.state.visibility.externalSystem,
        onFilterDropdownVisibleChange: visible => {
          this.setState(prevState => ({
            visibility: {
              ...prevState.visibility,
              externalSystem: visible,
            },
          }));
        },
        filterIcon: (
          <FilterFilled
            style={{
              color:
                filters.externalSystems && filters.externalSystems.length > 0
                  ? '#38b2a6'
                  : '#c8c9cb',
              backgroundColor: 'transparent',
            }}
          />
        ),
      },
      {
        title: t('cms.landlord.property_related.display_city.table'),
        dataIndex: 'displayCity',
        width: '10%',
        key: 'displayCity',
        render: displayCity => displayCity || '-',
        filterDropdown: (
          <TableColumnSearch
            searchType="city"
            isLocationCustom
            placeholder={t('cms.landlord.property_related.table.column.search.city_name')}
            onSearch={slug => {
              this.setState(
                prevState => ({
                  filters: {
                    ...prevState.filters,
                    citySlug: slug,
                    pageNumber: 1,
                  },
                  visibility: {
                    ...prevState.visibility,
                    displayCity: false,
                  },
                }),
                () => {
                  this.getNewProperties();
                },
              );
            }}
            onChange={value => {
              this.setState(prevState => ({
                ...prevState,
                cityFilter: value,
              }));
            }}
            t={t}
          />
        ),
        filterIcon: (
          <FilterFilled
            style={{
              color: filters.citySlug ? '#38b2a6' : '#c8c9cb',
              backgroundColor: 'transparent',
            }}
          />
        ),
        filterDropdownVisible: this.state.visibility.displayCity,
        onFilterDropdownVisibleChange: view => {
          if (!view && !this.state.cityFilter) {
            this.setState(
              prevState => ({
                filters: {
                  ...prevState.filters,
                  citySlug: null,
                  pageNumber: 1,
                },
                visibility: {
                  ...prevState.visibility,
                  displayCity: view,
                },
              }),
              () => {
                this.getNewProperties();
              },
            );
          } else {
            this.setState(prevState => ({
              visibility: {
                ...prevState.visibility,
                displayCity: view,
              },
            }));
          }
        },
      },
      {
        title: t('cms.landlord.property_related.display_country.table'),
        dataIndex: 'displayCountry',
        width: '10%',
        key: 'displayCountry',
        render: displayCountry => displayCountry || '-',
        filterDropdown: (
          <div>
            <label
              ref={node => {
                this.displayCountryTargetNode = node;
              }}
            />
            <SearchComponent
              t={t}
              targetInput={this.displayCountryTargetNode}
              options={displayCountryList}
              onBlur={value => {
                const countrySlugs = value.value.map(country => country.slug);
                if (isEqual(this.state.filters.countrySlugs, countrySlugs)) return;
                this.setState(
                  prevState => ({
                    filters: {
                      ...prevState.filters,
                      countrySlugs,
                      pageNumber: 1,
                    },
                  }),
                  () => {
                    this.getNewProperties();
                  },
                );
              }}
              onChange={value => {
                if (!value.currentItem) {
                  this.setState({
                    displayCountryList: [...this.state.displayCountryList],
                  });
                }
              }}
              keyValue="slug"
              showSelectAll={false}
            />
          </div>
        ),
        filterIcon: (
          <SearchOutlined
            style={{ color: filters.countrySlugs.length > 0 ? '#38b2a6' : '#c8c9cb' }}
          />
        ),
        filterDropdownVisible: this.state.visibility.displayCountry,
        onFilterDropdownVisibleChange: view => {
          if (view && this.displayCountryTargetNode) {
            setTimeout(() => {
              this.displayCountryTargetNode.click();
            }, 0);
          }
          if (view && displayCountryList.length === 0) {
            this.props.getDisplayCountryList(res => {
              this.setState({
                displayCountryList: res.countries.edges.map(country => country.node),
              });
            });
          }
          this.setState(prevState => ({
            visibility: {
              ...prevState.visibility,
              displayCountry: view,
            },
          }));
        },
      },
      {
        title: t('cms.landlord.property_related.status.table'),
        dataIndex: 'status',
        width: '10%',
        key: 'status',
        render: status => status || '-',
        filters: [
          {
            text: t(
              `cms.landlord.property_related.${propertyStatus.PUBLISHED.toLowerCase()}.option`,
            ),
            value: propertyStatus.PUBLISHED,
          },
          {
            text: t(
              `cms.landlord.property_related.${propertyStatus.UNPUBLISHED.toLowerCase()}.option`,
            ),
            value: propertyStatus.UNPUBLISHED,
          },
          {
            text: t(`cms.landlord.property_related.${propertyStatus.EDITING.toLowerCase()}.option`),
            value: propertyStatus.EDITING,
          },
          {
            text: t(`cms.landlord.property_related.${propertyStatus.NEW.toLowerCase()}.option`),
            value: propertyStatus.NEW,
          },
        ],
        filterMultiple: false,
        filteredValue: filters.statuses,
        filterIcon: (
          <FilterFilled
            style={{
              color: filters.statuses && filters.statuses.length > 0 ? '#38b2a6' : '#c8c9cb',
              backgroundColor: 'transparent',
            }}
          />
        ),
      },
      {
        title: t('cms.landlord.detail.ap_category.label'),
        dataIndex: 'apCategory',
        width: '10%',
        key: 'apCategory',
        render: (text, record) => {
          if (
            [
              bookingJourneyType.MANUAL,
              bookingJourneyType.LEAD_GENERATION,
              bookingJourneyType.FULL_AUTOMATIC,
            ].some(
              item => t(`properties_booking_type_${item.toLowerCase()}`) === record.bookingJourney,
            )
          ) {
            return t('cms.landlord.property.not_applicable');
          } else {
            return record?.apCategory
              ? t(`cms.landlord.modal.ap_category.${record.apCategory?.toLowerCase()}.option`)
              : '-';
          }
        },
      },
      {
        title: t('cms.landlord.detail.ap_category.deposit'),
        dataIndex: 'depositTransferTime',
        width: '12%',
        key: 'depositTransferTime',
        render: (text, record) => {
          if (
            [
              bookingJourneyType.MANUAL,
              bookingJourneyType.LEAD_GENERATION,
              bookingJourneyType.FULL_AUTOMATIC,
            ].some(
              item => t(`properties_booking_type_${item.toLowerCase()}`) === record.bookingJourney,
            )
          ) {
            return t('cms.landlord.property.not_applicable');
          } else {
            return record?.depositTransferTime
              ? t(
                  `cms.landlord.detail.ap_category.deposit.${record.depositTransferTime?.toLowerCase()}`,
                )
              : '-';
          }
        },
      },
      {
        title: t('cms.landlord.detail.ap_category.rent'),
        dataIndex: 'rentTransferTime',
        width: '12%',
        key: 'rentTransferTime',
        render: (text, record) => {
          if (
            [
              bookingJourneyType.MANUAL,
              bookingJourneyType.LEAD_GENERATION,
              bookingJourneyType.FULL_AUTOMATIC,
            ].some(
              item => t(`properties_booking_type_${item.toLowerCase()}`) === record.bookingJourney,
            )
          ) {
            return t('cms.landlord.property.not_applicable');
          } else {
            return record?.rentTransferTime
              ? t(`cms.landlord.detail.ap_category.rent.${record.rentTransferTime.toLowerCase()}`)
              : '-';
          }
        },
      },
      {
        title: t('cms.landlord.property_related.book_now_enable.table'),
        dataIndex: 'bookNowEnable',
        width: '20%',
        key: 'bookNowEnable',
        render: bookNowEnable => {
          if (bookNowEnable) {
            return <CheckOutlined style={{ color: '#419061' }} />;
          }
          return <CloseOutlined style={{ color: '#63656a' }} />;
        },
        filters: [
          { text: t('cms.landlord.property_related.enable.option'), value: true },
          { text: t('cms.landlord.property_related.disable.option'), value: false },
        ],
        filterMultiple: false,
        filteredValue: filters.bookNowEnable,
        filterIcon: (
          <FilterFilled
            style={{
              color:
                filters.bookNowEnable && filters.bookNowEnable.length > 0 ? '#38b2a6' : '#c8c9cb',
              backgroundColor: 'transparent',
            }}
          />
        ),
      },
      {
        title: 'Action',
        key: 'operation',
        fixed: 'right',
        width: 140,
        render: (text, record) => (
          <div
            className="landlord-property-related__view-details"
            onClick={() => {
              this.props.history.push(
                generatePath(
                  'property.homepage',
                  { propertySlug: record.slug },
                  { propertyId: record.id },
                ),
              );
            }}
          >
            <span>{t('cms.properties.edit.review_modal.button.view_detail')}</span>
          </div>
        ),
      },
    ];
  };

  handleShowBulkModal = () => {
    this.setState({ isBulkEditModalVisible: true });
  };
  render() {
    const { t, properties, isLoading } = this.props;

    return (
      <div className="landlord-property-related">
        <Choose>
          <When condition={properties.length === 0 && this.state.isWithoutFilter}>
            <div className="landlord-property-related__no-property-wrap">
              <LandlordNoPropertyIcon className="landlord-property-related__landlord-no-property" />
              <div className="landlord-property-related__description">
                {t('cms.landlord.property_related.no_property.description')}
              </div>
              <Button
                type="primary"
                size="large"
                onClick={this.props.handleCreateProperty}
                className="landlord-property-related__create-property"
              >
                {t('cms.landlord.property_related.create_property.button')}
              </Button>
            </div>
          </When>
          <Otherwise>
            <div className="landlord-property-related__tableBox">
              <div className="landlord-property-related__topBox">
                <span className="landlord-property-related__total-properties">
                  {t('cms.landlord.property.total_properties', {
                    num: this.props?.activePropertiesCount,
                  })}
                </span>
                <button
                  onClick={() => {
                    this.handleShowBulkModal();
                  }}
                  className="landlord-property-related__bulk-edit-btn"
                >
                  {t('cms.landlord.bulk_edit.btn')}
                </button>
              </div>

              <Table
                rowKey="id"
                scroll={{ x: 1580 }}
                columns={this.getTableColumns()}
                className="table-list"
                dataSource={properties}
                onChange={this.handleTableChange}
                loading={isLoading}
                pagination={{
                  showSizeChanger: true,
                  total: this.props.activePropertiesCount,
                  current: this.state.filters.pageNumber,
                  pageSize: this.state.filters.pageSize,
                }}
              />
            </div>
          </Otherwise>
        </Choose>
        <If condition={this.state.isBulkEditModalVisible}>
          <BulkEditModal
            visible={this.state.isBulkEditModalVisible}
            handleCloseModal={() => {
              this.setState({ isBulkEditModalVisible: false });
            }}
            getPropertyTabData={this.getNewProperties}
            t={this.props.t}
            propertyFilter={{
              ...this.state.filters,
              landlordSlug: this.props.landlordSlug,
              bookNowEnable: this.state.filters.bookNowEnable[0],
              bookingJourneys: ['SEMI_AUTOMATIC', 'INSTANT_BOOK'],
            }}
          />
        </If>
      </div>
    );
  }
}

LandlordPropertyRelated.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  properties: PropTypes.array,
  handleCreateProperty: PropTypes.func.isRequired,
  activePropertiesCount: PropTypes.number,
  getPropertiesByLandlordSlug: PropTypes.func.isRequired,
  landlordSlug: PropTypes.string,
  isLoading: PropTypes.bool,
  searchPropertyNameList: PropTypes.func,
  getDisplayCountryList: PropTypes.func.isRequired,
};

LandlordPropertyRelated.defaultProps = {
  t: () => {},
  properties: [],
  handleCreateProperty: () => {},
  activePropertiesCount: 0,
  getPropertiesByLandlordSlug: () => {},
  landlordSlug: '',
  isLoading: false,
  searchPropertyNameList: () => {},
  getDisplayCountryList: () => {},
};
